import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import DialogFormFooter from '@/components/DialogFormFooter';
import AddressEditor from '@/components/entities/AddressEditor';
import PersonPicker from '@/components/entities/PersonPicker';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, X, Users as UsersIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const emptyForm = () => ({ id: '', name: '', notes: '', addresses: [], contacts: [], primary_contact_id: null });

const toForm = (o) => ({
  id: o.id, name: o.name || '', notes: o.notes || '',
  addresses: (o.addresses || []).map((a) => ({ ...a })),
  contacts: (o.contacts || []).map((c) => ({ ...c })),
  primary_contact_id: o.primary_contact_id || null,
});

/**
 * Create/edit an Organization. Its contacts are People (each an OrgContact with
 * a role and an is_primary flag). Reused by the Organizations directory page and
 * by OrgPicker's inline "new organization" action. onSaved(org) with the record.
 */
const OrgFormDialog = ({ open, onOpenChange, organization = null, onSaved, initialName = '' }) => {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [persons, setPersons] = useState([]);

  const fetchPersons = useCallback(async () => {
    try {
      const res = await api.get('/persons');
      setPersons(res.data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchPersons();
    setForm(organization ? toForm(organization) : { ...emptyForm(), name: initialName });
  }, [open, organization, initialName, fetchPersons]);

  const onPersonCreated = (p) => setPersons((prev) => [p, ...prev.filter((x) => x.id !== p.id)]);

  const addContact = () =>
    setForm((f) => {
      const first = f.contacts.length === 0;
      return {
        ...f,
        contacts: [...f.contacts, { person_id: '', role: '', is_primary: first }],
      };
    });
  const updateContact = (idx, field, v) =>
    setForm((f) => ({ ...f, contacts: f.contacts.map((c, i) => (i === idx ? { ...c, [field]: v } : c)) }));
  const removeContact = (idx) =>
    setForm((f) => {
      const contacts = f.contacts.filter((_, i) => i !== idx);
      const stillPrimary = contacts.some((c) => c.is_primary);
      if (!stillPrimary && contacts.length) contacts[0].is_primary = true;
      return { ...f, contacts };
    });
  const setPrimaryContact = (idx) =>
    setForm((f) => ({ ...f, contacts: f.contacts.map((c, i) => ({ ...c, is_primary: i === idx })) }));

  const chosenIds = form.contacts.map((c) => c.person_id).filter(Boolean);

  const save = async () => {
    if (form.contacts.some((c) => !c.person_id)) {
      toast.error('Every contact must reference a person');
      return;
    }
    setSubmitting(true);
    try {
      const primary = form.contacts.find((c) => c.is_primary);
      const payload = {
        id: organization?.id || uuidv4(),
        name: form.name.trim(),
        notes: form.notes.trim() || null,
        addresses: form.addresses,
        contacts: form.contacts.map((c) => ({ person_id: c.person_id, role: c.role.trim() || null, is_primary: !!c.is_primary })),
        primary_contact_id: primary ? primary.person_id : null,
      };
      const res = organization
        ? await api.put(`/organizations/${payload.id}`, payload)
        : await api.post('/organizations', payload);
      toast.success(organization ? 'Organization updated' : 'Organization created');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-bold text-primary">
            {organization ? 'Edit Organization' : 'New Organization'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4" data-testid="org-form">
          <div>
            <Label htmlFor="of-name">Name *</Label>
            <Input id="of-name" data-testid="of-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium flex items-center gap-2"><UsersIcon size={16} /> Contacts</h4>
              <Button type="button" size="sm" variant="outline" onClick={addContact} data-testid="add-contact-btn">
                <Plus size={14} /> Add contact
              </Button>
            </div>
            {form.contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contacts yet. An organization inherits its contact details from its primary contact.</p>
            ) : (
              <div className="space-y-2">
                {form.contacts.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center border rounded-md p-2" data-testid={`org-contact-row-${idx}`}>
                    <div className="sm:col-span-6">
                      <PersonPicker
                        value={c.person_id}
                        onChange={(v) => updateContact(idx, 'person_id', v)}
                        persons={persons}
                        onCreated={onPersonCreated}
                        excludeIds={chosenIds.filter((id) => id !== c.person_id)}
                        placeholder="Select contact person…"
                        testId={`org-contact-person-${idx}`}
                      />
                    </div>
                    <Input className="sm:col-span-4" placeholder="Role (e.g. General Counsel)" value={c.role} onChange={(e) => updateContact(idx, 'role', e.target.value)} aria-label="Contact role" />
                    <label className="sm:col-span-1 flex items-center gap-1 text-xs cursor-pointer">
                      <input type="radio" name="org-primary-contact" checked={!!c.is_primary} onChange={() => setPrimaryContact(idx)} aria-label="Set as primary contact" />
                      Primary
                    </label>
                    <button type="button" className="sm:col-span-1 text-muted-foreground hover:text-destructive justify-self-end" onClick={() => removeContact(idx)} aria-label="Remove contact">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <AddressEditor value={form.addresses} onChange={(addresses) => setForm({ ...form, addresses })} label="Addresses" />
          <div>
            <Label htmlFor="of-notes">Notes</Label>
            <Textarea id="of-notes" data-testid="of-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFormFooter
            onCancel={() => onOpenChange(false)}
            onSubmit={save}
            submitLabel={organization ? 'Update Organization' : 'Create Organization'}
            disabled={!form.name.trim() || submitting}
            submitTestId="save-org-btn"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrgFormDialog;
